import type { IIndirectTaxTaxTypeOptionV2 } from '@commenda/api/indirect-tax/registrations/types';
import {
  IIndirectTaxTypeDetails,
  IndirectTaxJurisdictionType,
} from '@commenda/api/indirect-tax/registrations/types';
import {
  GovernmentIdentifierPurpose,
  IGovernmentIdentifier,
} from '@commenda/types';
import { State } from 'country-state-city';
import { getCountries, type CountryCode } from 'libphonenumber-js';

import { getDefaultCurrency } from '../../../form-builder/config/country-bank-utils';
import { IndirectTaxRegionHelper } from '../../common/helpers/IndirectTaxRegionHelper';
import { IndirectTaxRegion } from '../../common/types';
import {
  NonUKBankAccountReason,
  UKExemptSuppliesAnswer,
  UKPresenceType,
  UKVATRegistrationReason,
} from '../wizards/new-state/constants';

export type IndirectTaxRegistrationTaxTypeDetailsMap = Record<
  string,
  IIndirectTaxTypeDetails
>;

export interface IndirectTaxRegistrationTaxTypeRecommendationBadge {
  label: string;
  variant: 'blue' | 'success' | 'gray';
}

export interface ParsedFrequencyOption {
  primary: string;
  allValues: string[];
  label: string;
}

interface IndirectTaxRegistrationLocation {
  jurisdictionId: string;
  country?: string | null;
}

export class IndirectTaxRegistrationHelper {
  // --- Government identifiers ---

  /** The national tax number a country issues, e.g. the EIN for the US. An entity may hold one from a country it is not incorporated in. */
  static findNationalTaxIdentifier(
    governmentIdentifiers: ReadonlyArray<IGovernmentIdentifier> | undefined,
    country: string | undefined,
    isCountryLevel: (jurisdictionId?: string | null) => boolean
  ): IGovernmentIdentifier | undefined {
    if (!country) return undefined;
    const nationalTaxIdentifiers = governmentIdentifiers?.filter(
      (identifier) =>
        identifier.country === country &&
        identifier.isFunctionCritical &&
        !identifier.isCustom &&
        (!identifier.jurisdictionId ||
          isCountryLevel(identifier.jurisdictionId)) &&
        identifier.purposes.includes(GovernmentIdentifierPurpose.TAXATION)
    );
    // Several can qualify — the UK has the UTR and the VAT number — so the catalog's primary ruling picks first.
    return (
      nationalTaxIdentifiers?.find((identifier) => identifier.isPrimaryTaxId) ??
      nationalTaxIdentifiers?.[0]
    );
  }

  // --- Jurisdiction classification ---

  static readonly STATE_LEVEL_JURISDICTION_TYPES: string[] = [
    IndirectTaxJurisdictionType.STATE_OR_PROVINCE,
    IndirectTaxJurisdictionType.STATE,
    IndirectTaxJurisdictionType.COUNTRY,
    IndirectTaxJurisdictionType.TRADEBLOC,
    IndirectTaxJurisdictionType.TERRITORY,
  ];

  static readonly LOCAL_JURISDICTION_TYPES: string[] = [
    IndirectTaxJurisdictionType.COUNTY,
    IndirectTaxJurisdictionType.CITY,
    IndirectTaxJurisdictionType.DISTRICT,
    IndirectTaxJurisdictionType.LOCAL,
    IndirectTaxJurisdictionType.RESERVATION,
  ];

  static readonly JURISDICTION_TYPE_LABELS: Record<string, string> = {
    [IndirectTaxJurisdictionType.COUNTY]: 'County',
    [IndirectTaxJurisdictionType.CITY]: 'City',
    [IndirectTaxJurisdictionType.DISTRICT]: 'District',
    [IndirectTaxJurisdictionType.LOCAL]: 'Local',
    [IndirectTaxJurisdictionType.RESERVATION]: 'Reservation',
    [IndirectTaxJurisdictionType.TERRITORY]: 'Territory',
  };

  static getJurisdictionTypeLabel(
    jurisdictionType: string | undefined | null
  ): string | undefined {
    if (!jurisdictionType) return undefined;
    return this.JURISDICTION_TYPE_LABELS[jurisdictionType] || jurisdictionType;
  }

  static isLocalJurisdiction(
    jurisdictionType: string | undefined | null
  ): boolean {
    if (!jurisdictionType) return false;
    return !this.STATE_LEVEL_JURISDICTION_TYPES.includes(jurisdictionType);
  }

  // --- Country / Region from jurisdiction ID ---

  // this should come from the api.
  static getCountryFromJurisdictionId(jurisdictionId: string): string {
    if (!jurisdictionId) return '';
    if (jurisdictionId.startsWith('STATE_CEN_')) return 'US';
    if (jurisdictionId.startsWith('TRADEBLOC_EU_')) return 'EU';
    if (jurisdictionId.startsWith('PROVINCE_1008')) return 'CA';
    if (jurisdictionId.startsWith('COUNTRY_')) {
      const parts = jurisdictionId.split('_');
      return parts[1] || '';
    }
    return '';
  }

  private static getRegistrationCountry(
    location: IndirectTaxRegistrationLocation
  ): string {
    return (
      location.country ||
      this.getCountryFromJurisdictionId(location.jurisdictionId)
    );
  }

  static getRegistrationRegion(
    location: IndirectTaxRegistrationLocation
  ): IndirectTaxRegion {
    return IndirectTaxRegionHelper.getRegionFromCountryCode(
      this.getRegistrationCountry(location)
    );
  }

  static getRegistrationCurrencyCode(
    location: IndirectTaxRegistrationLocation
  ): string {
    const country = this.getRegistrationCountry(location);
    return country === 'EU' ? 'EUR' : getDefaultCurrency(country);
  }

  static getTaxableSalesCurrencyCode(
    savedCurrency: string | null | undefined,
    defaultCurrency: string
  ): string {
    if (savedCurrency?.trim()) {
      return savedCurrency;
    }
    return defaultCurrency;
  }

  static getRegistrationFlagCountry(country: string): CountryCode | undefined {
    return getCountries().find((code) => code === country);
  }

  // --- Tax type display ---

  static getTaxTypeRecommendationBadge(
    recommendedFor?: string[]
  ): IndirectTaxRegistrationTaxTypeRecommendationBadge | null {
    if (!recommendedFor || recommendedFor.length === 0) return null;

    const hasInState = recommendedFor.includes('IN_STATE');
    const hasOutOfState = recommendedFor.includes('OUT_OF_STATE');

    if (hasInState && hasOutOfState) {
      return {
        label: 'Recommended for in-state and out-of-state businesses',
        variant: 'gray',
      };
    }
    if (hasInState) {
      return { label: 'Recommended for in-state businesses', variant: 'blue' };
    }
    if (hasOutOfState) {
      return {
        label: 'Recommended for out-of-state businesses',
        variant: 'success',
      };
    }
    return null;
  }

  static getTaxTypeDisplayName(
    taxType: string,
    taxTypeDetailsMap?: IndirectTaxRegistrationTaxTypeDetailsMap
  ): string {
    return taxTypeDetailsMap?.[taxType]?.displayName || taxType;
  }

  static getTaxTypeLabel(
    option: IIndirectTaxTaxTypeOptionV2,
    taxTypeDetailsMap?: IndirectTaxRegistrationTaxTypeDetailsMap
  ): string {
    const requiredRelated =
      option.taxTypes.related
        ?.filter((r) => !r.optional)
        .map((r) => this.getTaxTypeDisplayName(r.value, taxTypeDetailsMap)) ||
      [];

    const main = this.getTaxTypeDisplayName(
      option.taxTypes.primary,
      taxTypeDetailsMap
    );

    if (requiredRelated.length > 0) {
      return `${main}, ${requiredRelated.join(', ')}`;
    }
    return main;
  }

  // --- Tax type option parsing ---

  static getOptionalTaxTypes(option: IIndirectTaxTaxTypeOptionV2): string[] {
    return (
      option.taxTypes.related?.filter((r) => r.optional).map((r) => r.value) ||
      []
    );
  }

  static getRequiredTaxTypes(option: IIndirectTaxTaxTypeOptionV2): string[] {
    const requiredRelated =
      option.taxTypes.related?.filter((r) => !r.optional).map((r) => r.value) ||
      [];
    return [option.taxTypes.primary, ...requiredRelated];
  }

  // --- Frequency options ---

  static getFrequencyOptions(
    options: IIndirectTaxTaxTypeOptionV2[],
    formatFrequency: (value: string) => string
  ): ParsedFrequencyOption[] {
    const seen = new Set<string>();
    const result: ParsedFrequencyOption[] = [];

    options.forEach((opt) => {
      opt.frequencies.forEach((f) => {
        if (seen.has(f.primary)) return;
        seen.add(f.primary);

        const requiredRelated =
          f.related?.filter((r) => !r.optional).map((r) => r.value) ?? [];
        const allValues = [f.primary, ...requiredRelated];

        const label =
          allValues.length > 1
            ? `${formatFrequency(f.primary)} + ${requiredRelated.map(formatFrequency).join(', ')}`
            : formatFrequency(f.primary);

        result.push({ primary: f.primary, allValues, label });
      });
    });

    return result;
  }

  // --- Number formatting ---

  static formatNumberWithCommas(value: string | number): string {
    if (value === '' || value === undefined || value === null) return '';
    const numStr = value.toString().replace(/,/g, '');
    if (isNaN(Number(numStr))) return value.toString();
    return Number(numStr).toLocaleString('en-US');
  }

  // --- Supported US states for indirect tax exemption certificates ---

  static readonly SUPPORTED_STATE_CODES: string[] = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DC',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'NE',
    'NV',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'WA',
    'WV',
    'WI',
    'WY',
    'VA',
  ];

  static getSupportedStatesForIndirectTax(): {
    value: string;
    label: string;
  }[] {
    return State.getStatesOfCountry('US')
      .map((state) => ({ value: state.isoCode, label: state.name }))
      .filter((state) => this.SUPPORTED_STATE_CODES.includes(state.value));
  }

  // --- HMRC reasons for non-UK remittance accounts (UK registrations only) ---

  static readonly NON_UK_BANK_ACCOUNT_REASON_OPTIONS: {
    value: NonUKBankAccountReason;
    label: string;
  }[] = [
    {
      value: NonUKBankAccountReason.ACCOUNT_BEING_SET_UP,
      label: 'Account is being set up',
    },
    {
      value: NonUKBankAccountReason.WAITING_FOR_BANK_DETAILS,
      label: 'Waiting for bank details from the bank',
    },
    {
      value: NonUKBankAccountReason.OVERSEAS_BANK_ACCOUNT,
      label: 'Business has an overseas bank account',
    },
    {
      value: NonUKBankAccountReason.WAITING_FOR_NAME_CHANGE,
      label: 'Waiting for a change of name at the bank',
    },
  ];

  // --- UK activity & presence (UK registrations only) ---

  static readonly UK_VAT_REGISTRATION_REASON_OPTIONS: {
    value: UKVATRegistrationReason;
    label: string;
  }[] = [
    {
      value: UKVATRegistrationReason.SALES_TO_UK_CUSTOMERS,
      label: 'Sales to UK customers',
    },
    {
      value: UKVATRegistrationReason.STORING_GOODS_IN_UK,
      label: 'Storing goods in the UK',
    },
    {
      value: UKVATRegistrationReason.IMPORTING_GOODS,
      label: 'Importing goods',
    },
    {
      value: UKVATRegistrationReason.DISTANCE_SELLING,
      label: 'Distance selling',
    },
    {
      value: UKVATRegistrationReason.MARKETPLACE_FACILITATION,
      label: 'Marketplace facilitation',
    },
    {
      value: UKVATRegistrationReason.TRANSFER_OF_GOING_CONCERN,
      label: 'Transfer of going concern (TOGC)',
    },
    {
      value: UKVATRegistrationReason.OTHER,
      label: 'Other',
    },
  ];

  static readonly UK_EXEMPT_SUPPLIES_ANSWER_OPTIONS: {
    value: UKExemptSuppliesAnswer;
    label: string;
  }[] = [
    { value: UKExemptSuppliesAnswer.YES, label: 'Yes' },
    { value: UKExemptSuppliesAnswer.NO, label: 'No' },
    { value: UKExemptSuppliesAnswer.NOT_SURE, label: 'Not sure' },
  ];

  static readonly UK_PRESENCE_TYPE_OPTIONS: {
    value: UKPresenceType;
    label: string;
  }[] = [
    { value: UKPresenceType.WAREHOUSE, label: 'Warehouse' },
    { value: UKPresenceType.REPRESENTATIVE, label: 'Representative' },
    { value: UKPresenceType.STAFF, label: 'Staff' },
    { value: UKPresenceType.NONE, label: 'None' },
  ];
}
